import React from 'react';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as SonnerToaster } from '@/components/ui/sonner';
import AddAdvisorModal from './AddAdvisorModal';
import { AccessRequestDetailModal } from './AccessRequestDetailModal';
import { useAdminDashboard } from './hooks/useAdminDashboard';
import { AdminRoleTabContent } from './admin/AdminRoleTabContent';
import { AdminCoreTabContent } from './admin/AdminCoreTabContent';
import EstateVaultAdminStrip from './admin/EstateVaultAdminStrip';

const AdminDashboard = () => {
  const {
    admin, activeTab, setActiveTab, filter, setFilter,
    accessRequests, loading, actionLoading,
    usersRefreshKey, selectedRequest, setSelectedRequest,
    showAddAdvisorModal, setShowAddAdvisorModal,
    visibleTabs, visibleRoleTabs, isRoleAllowed,
    fetchUsers, handleApprove, handleWaitlist, handleDeleteUser, handleLogout,
    toast, setAdmin,
  } = useAdminDashboard();

  const renderTabButton = (tab, onClick) => (
    <button key={tab.key} onClick={onClick || (() => setActiveTab(tab.key))} data-testid={`tab-${tab.key}`}
      className={`px-3 py-2 sm:px-6 sm:py-3 rounded-lg text-xs sm:text-sm font-semibold transition ${
        activeTab === tab.key ? 'bg-green-500 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}>
      {tab.label}
    </button>
  );

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-black">
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-linear-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-white">Admin Dashboard</h1>
                <p className="text-xs sm:text-sm text-gray-400 hidden sm:block">Access Request Management</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 sm:space-x-4">
              {admin && (
                <div className="text-right hidden sm:block">
                  <p className="text-sm text-white font-medium">{admin.name}</p>
                  <p className="text-xs text-gray-400">{admin.email}</p>
                </div>
              )}
              <button onClick={handleLogout} className="px-3 sm:px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-xs sm:text-sm font-medium transition">
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {admin?.role !== 'infosec' && (
          <EstateVaultAdminStrip onGoToVault={() => setActiveTab('estate-vault')} />
        )}
        {admin?.role !== 'infosec' && (
          <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
            <h2 className="text-lg sm:text-xl font-bold text-white">Access Requests</h2>
            <button onClick={() => setShowAddAdvisorModal(true)} className="px-3 sm:px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
              <span>Add Trusted Professional</span>
            </button>
          </div>
        )}
        <div className="bg-gray-900 rounded-xl p-2 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-wrap gap-1.5 sm:gap-2">
            {admin?.role !== 'infosec' && visibleTabs.map(tab =>
              renderTabButton(tab, tab.key === 'users' ? () => setActiveTab('users') : undefined)
            )}
            {visibleRoleTabs.map(tab => renderTabButton(tab))}
          </div>
        </div>

        <AdminCoreTabContent activeTab={activeTab}
          accessRequests={accessRequests} loading={loading} filter={filter} setFilter={setFilter}
          actionLoading={actionLoading} onApprove={handleApprove} onWaitlist={handleWaitlist} onSelectRequest={setSelectedRequest}
          usersRefreshKey={usersRefreshKey} onDeleteUser={handleDeleteUser} fetchUsers={fetchUsers}
          admin={admin} setAdmin={setAdmin} />
      </div>

      <Toaster />
      <SonnerToaster richColors position="top-center" />

      <AdminRoleTabContent activeTab={activeTab} admin={admin} isRoleAllowed={isRoleAllowed} />

      {activeTab !== 'pricing' && (
        <AddAdvisorModal isOpen={showAddAdvisorModal} onClose={() => setShowAddAdvisorModal(false)} onSuccess={(data) => toast({ title: "Trusted Professional Created", description: `${data.advisor_id} - Portal link sent to email`, className: "bg-green-600 text-white border-green-700" })} />
      )}
      <AccessRequestDetailModal request={selectedRequest} onClose={() => setSelectedRequest(null)} onApprove={handleApprove} onWaitlist={handleWaitlist} />
    </div>
  );
};

export default AdminDashboard;
